import {
	Message,
	CollectionRegistry,
	DashboardAgentRequestMessageSchema,
	DashboardAgentAbortRequestMessageSchema,
} from '../types';
import { logger } from '../utils/logger';

// ============================================================
// Abort / cancellation registry — mirrors handlers/user-prompt-request.ts
// exactly (inFlightAborts + tombstone-for-early-abort), keyed by dashboardId
// instead of uiBlockId. One dashboard-agent run per dashboardId at a time
// (the backend's own collection additionally locks on this), so dashboardId
// is a safe, simple key here too.
// ============================================================
const inFlightAborts = new Map<string, AbortController>();
const recentlyAborted = new Map<string, number>();
const ABORT_TOMBSTONE_TTL_MS = 30_000;

function setAbortTombstone(dashboardId: string): void {
	const now = Date.now();
	recentlyAborted.set(dashboardId, now);
	for (const [key, ts] of recentlyAborted) {
		if (now - ts > ABORT_TOMBSTONE_TTL_MS) recentlyAborted.delete(key);
	}
}

function consumeAbortTombstone(dashboardId: string): boolean {
	const ts = recentlyAborted.get(dashboardId);
	if (ts === undefined) return false;
	recentlyAborted.delete(dashboardId);
	return Date.now() - ts <= ABORT_TOMBSTONE_TTL_MS;
}

/**
 * Handle a DASHBOARD_AGENT_ABORT_REQ (frontend "Stop" button). Cancels the
 * in-flight run for the given dashboardId, or tombstones it so a run that's
 * about to start cancels itself immediately (handles the abort-arrives-first
 * race).
 */
export function handleDashboardAgentAbort(
	data: any,
	sendMessage: (message: Message) => void
): void {
	const parseResult = DashboardAgentAbortRequestMessageSchema.safeParse(data);
	if (!parseResult.success) {
		logger.warn('[DashboardAgentAbort] Invalid message — ignoring');
		return;
	}
	const { payload, from } = parseResult.data;
	const { dashboardId } = payload;
	const wsId = from.id;

	const controller = inFlightAborts.get(dashboardId);
	if (controller) {
		controller.abort();
		inFlightAborts.delete(dashboardId);
		logger.info(`[DashboardAgentAbort] Aborted in-flight run for dashboard ${dashboardId}`);
	} else {
		setAbortTombstone(dashboardId);
		logger.info(`[DashboardAgentAbort] No in-flight run for dashboard ${dashboardId} — tombstoned`);
	}

	try {
		sendMessage({
			id: `dashboard_agent_aborted_${dashboardId}`,
			type: 'DASHBOARD_AGENT_ABORTED',
			from: { type: 'data-agent' },
			to: { type: 'runtime', id: wsId },
			payload: { dashboardId },
		});
	} catch (err) {
		logger.warn(`[DashboardAgentAbort] Failed to send abort ack: ${err instanceof Error ? err.message : String(err)}`);
	}
}

/**
 * Handle DASHBOARD_AGENT_REQ. The actual "spawn Pi" work lives in the
 * backend's own 'dashboard-agent'.'run' collection (deployment-specific —
 * see project_sdk_node_dependency reasoning: this SDK stays generic). This
 * handler owns the generic, reusable parts: parsing, the abort registry +
 * tombstone, streaming progress back over the SAME connection, and sending
 * the final response — identical shape to how USER_PROMPT_REQ does it.
 */
export async function handleDashboardAgentRequest(
	data: any,
	collections: CollectionRegistry | undefined,
	sendMessage: (message: Message) => void
): Promise<void> {
	console.log('[DashboardAgentReq] Handler invoked. Raw message type/id:', data?.type, data?.id);

	const parseResult = DashboardAgentRequestMessageSchema.safeParse(data);
	if (!parseResult.success) {
		logger.error('[DashboardAgentReq] Invalid message — failed schema validation:', JSON.stringify(parseResult.error.errors));
		return;
	}

	const { id, payload, from } = parseResult.data;
	const { prompt, dashboardId, projectId, userId, selectedWidgetId } = payload;
	const wsId = from.id;

	console.log(`[DashboardAgentReq] Parsed OK. dashboardId=${dashboardId}, wsId=${wsId}, collectionsAvailable=${!!collections}, hasDashboardAgentCollection=${!!collections?.['dashboard-agent']?.['run']}`);

	const respond = (res: { success: boolean; error?: string; dashboard?: any }) => {
		sendMessage({
			id,
			type: 'DASHBOARD_AGENT_RES',
			from: { type: 'data-agent' },
			to: { type: 'runtime', id: wsId },
			payload: res,
		});
	};

	if (!collections?.['dashboard-agent']?.['run']) {
		respond({ success: false, error: "Dashboard agent is not available in this environment (collection 'dashboard-agent' not registered)." });
		return;
	}

	const abortController = new AbortController();
	if (consumeAbortTombstone(dashboardId)) {
		abortController.abort();
		logger.info(`[DashboardAgentReq] dashboardId=${dashboardId} was pre-aborted — starting cancelled`);
	}
	inFlightAborts.set(dashboardId, abortController);

	// Monotonic per-request counter, stamped on every chunk as `seq`. Observed
	// duplicate chunk delivery (same id, same content, back-to-back) on
	// long-running dashboard-agent requests that isn't explained by anything
	// in this send path itself (confirmed single-send via dashboard.log) —
	// most likely a reconnect/health-check cycle on the frontend client
	// briefly overlapping two live sockets during a multi-minute run. Rather
	// than depend on fully diagnosing/patching that, this makes the frontend
	// robust to duplicate delivery regardless of its source: a chunk whose
	// seq isn't strictly greater than the last one processed gets discarded
	// as a repeat.
	let seq = 0;
	const onProgress = (chunk: string) => {
		try {
			sendMessage({
				// Keyed by this request's own id, not dashboardId — dashboardId
				// repeats across every request for the same dashboard, so a stream
				// listener that failed to clean up after a past request would keep
				// matching every future one forever, double-processing (or worse)
				// every chunk. Per-request id makes a stale listener simply not
				// match anything new, instead of silently compounding.
				id: `dashboard_agent_stream_${id}`,
				type: 'DASHBOARD_AGENT_STREAM',
				from: { type: 'data-agent' },
				to: { type: 'runtime', id: wsId },
				payload: { dashboardId, chunk, seq: seq++ },
			});
		} catch (err) {
			logger.warn(`[DashboardAgentReq] Stream send failed (WebSocket may be closed): ${err instanceof Error ? err.message : String(err)}`);
		}
	};

	try {
		console.log(`[DashboardAgentReq] Calling collections['dashboard-agent']['run'] for dashboardId=${dashboardId}...`);
		const result = await collections['dashboard-agent']['run']({
			prompt,
			dashboardId,
			projectId,
			userId,
			selectedWidgetId,
			signal: abortController.signal,
			onProgress,
		});
		console.log(`[DashboardAgentReq] collections['dashboard-agent']['run'] resolved for dashboardId=${dashboardId}: success=${result?.success}, hasDashboard=${!!result?.dashboard}, error=${result?.error ?? '(none)'}`);

		if (abortController.signal.aborted) {
			respond({ success: false, error: 'aborted' });
			return;
		}

		respond(result?.success
			? { success: true, dashboard: result.dashboard }
			: { success: false, error: result?.error || 'Dashboard agent failed' });
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		console.log(`[DashboardAgentReq] collections['dashboard-agent']['run'] THREW for dashboardId=${dashboardId}: ${errorMsg}`);
		logger.error(`[DashboardAgentReq] Error: ${errorMsg}`);
		respond({ success: abortController.signal.aborted ? false : false, error: abortController.signal.aborted ? 'aborted' : errorMsg });
	} finally {
		inFlightAborts.delete(dashboardId);
	}
}
